import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, AlertCircle, Info, X, AlertTriangle } from 'lucide-react';

const Toast = ({ toasts, removeToast }) => {
  const getToastConfig = (type) => {
    switch (type) {
      case 'success':
        return {
          icon: CheckCircle,
          color: 'from-emerald-500 to-green-600',
          bg: 'bg-emerald-50',
          border: 'border-emerald-200',
          text: 'text-emerald-900',
          iconBg: 'bg-emerald-100',
          iconColor: 'text-emerald-600',
        };
      case 'error':
        return {
          icon: AlertCircle,
          color: 'from-red-500 to-rose-600',
          bg: 'bg-red-50',
          border: 'border-red-200',
          text: 'text-red-900',
          iconBg: 'bg-red-100',
          iconColor: 'text-red-600',
        };
      case 'warning':
        return {
          icon: AlertTriangle,
          color: 'from-amber-500 to-orange-600',
          bg: 'bg-amber-50',
          border: 'border-amber-200',
          text: 'text-amber-900',
          iconBg: 'bg-amber-100',
          iconColor: 'text-amber-600',
        };
      case 'info':
      default:
        return {
          icon: Info,
          color: 'from-blue-500 to-indigo-600',
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          text: 'text-blue-900',
          iconBg: 'bg-blue-100',
          iconColor: 'text-blue-600',
        };
    }
  };

  return (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-3 max-w-md w-full px-4 sm:px-0">
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => {
          const config = getToastConfig(toast.type);
          const Icon = config.icon;

          return (
            <motion.div
              key={toast.id}
              layout
              initial={{ opacity: 0, y: -50, scale: 0.8, x: 100 }}
              animate={{ opacity: 1, y: 0, scale: 1, x: 0 }}
              exit={{ opacity: 0, x: 100, scale: 0.8, transition: { duration: 0.2 } }}
              transition={{ 
                type: 'spring', 
                stiffness: 300, 
                damping: 25,
                mass: 0.8
              }}
              className={`${config.bg} ${config.border} border-2 rounded-2xl shadow-2xl backdrop-blur-xl`}
            >
              {/* Left accent bar */}
              <div className={`absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b ${config.color}`} />

              {/* Progress bar */}
              <motion.div
                className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${config.color} origin-left`}
                initial={{ scaleX: 1 }}
                animate={{ scaleX: 0 }}
                transition={{ duration: toast.duration / 1000, ease: 'linear' }}
              />

              <div className="relative p-4 flex items-start gap-3">
                {/* Icon with animation */}
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
                  className={`flex-shrink-0 ${config.iconBg} p-2.5 rounded-xl`}
                >
                  <Icon className={`w-5 h-5 ${config.iconColor}`} />
                </motion.div>

                {/* Content */}
                <div className="flex-1 min-w-0 pt-0.5">
                  {toast.title && (
                    <motion.p
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.15 }}
                      className={`font-bold ${config.text} text-sm mb-0.5`}
                    >
                      {toast.title}
                    </motion.p>
                  )}
                  <motion.p
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className={`${config.text} text-sm font-medium leading-snug`}
                  >
                    {toast.message}
                  </motion.p>
                </div>

                {/* Close button */}
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => removeToast(toast.id)}
                  className={`flex-shrink-0 ${config.iconColor} hover:bg-white/50 p-1.5 rounded-lg transition-colors`}
                >
                  <X className="w-4 h-4" />
                </motion.button>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

export default Toast;

